import { ApolloProvider } from "@apollo/client";
import { alternatedMaterialUrl } from "@saleor/alternatedMaterial/urls";
import DemoBanner from "@saleor/components/DemoBanner";
import DialogCreateOrderError from "@saleor/createOrder/components/DialogCreateOrderError";
import { PermissionEnum } from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import { ThemeProvider } from "@saleor/macaw-ui";
import OrderConfirmSection from "@saleor/OrderConfirm";
import { orderConfirmUrl } from "@saleor/OrderConfirm/urls";
import PendingOrderSection from "@saleor/PendingOrder";
import {
  pendingOrderTrackingUrl,
  pendingOrderUrl
} from "@saleor/PendingOrder/urls";
import { useDerivedPermissions } from "@saleor/permissionHooks";
import { SaleorProvider } from "@saleor/sdk";
import StockOnHandSection from "@saleor/stockOnHand";
import { stockOnHandUrl } from "@saleor/stockOnHand/url";
import { Amplify } from "aws-amplify";
import React, { useEffect, useMemo, useState } from "react";
import { render } from "react-dom";
import ErrorBoundary from "react-error-boundary";
import TagManager from "react-gtm-module";
import { IdleTimerProvider } from "react-idle-timer";
import { useIntl } from "react-intl";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import LayoutProvider from "src/context/LayoutContext";

import AlternatedMaterialSection from "./alternatedMaterial";
import { ExternalAppProvider } from "./apps/components/ExternalAppContext";
import Auth, { getTokens, removeTokens, useUser } from "./auth";
import AuthProvider, { useAuth } from "./auth/AuthProvider";
import SectionRoute from "./auth/components/SectionRoute";
import ChangeOrderSection from "./changeOrder";
import { changeOrderUrl } from "./changeOrder/urls";
import AppLayout from "./components/AppLayout";
import { DateProvider } from "./components/Date";
import ExitFormDialogProvider from "./components/Form/ExitFormDialogProvider";
import { LocaleProvider } from "./components/Locale";
import MessageManagerProvider from "./components/messages";
import MyTypography from "./components/MyTypography";
import { ShopProvider } from "./components/Shop";
import { WindowTitle } from "./components/WindowTitle";
import {
  APP_MOUNT_URI,
  AWS_CONFIG,
  DEMO_MODE,
  GTM_ID,
  IDLE_TIMEOUT_MINUTE
} from "./config";
import AppStateProvider from "./containers/AppState";
import BackgroundTasksProvider from "./containers/BackgroundTasks";
import ServiceWorker from "./containers/ServiceWorker";
import CreateOrderNotRefSection from "./createOrChangeNotRefContract";
import {
  changeOrderNotRefUrl,
  createOrderNotRefUrl
} from "./createOrChangeNotRefContract/urls";
import CreateOrderSection from "./createOrder";
import { createOrderUrl } from "./createOrder/urls";
import CustomerBlockContextProvider from "./customerBlock/context/CustomerBlockContext";
import CustomerMaterialSection from "./customerMaterial";
import { customerMaterialUrl } from "./customerMaterial/urls";
import DeliveryReport from "./deliveryReport";
import { deliveryReport } from "./deliveryReport/urls";
import ExcelUploadPage from "./excelUpload";
import { excelUploadUrl } from "./excelUpload/urls";
import { apolloClient, saleorClient } from "./graphql/client";
import MainPage from "./homePage";
import HomePageComponent from "./homePage/views";
import useNotifier from "./hooks/useNotifier";
import Inquiry from "./inquirySearchOrder";
import { inquirySearchOrderUrl } from "./inquirySearchOrder/urls";
import { commonMessages, sectionNames } from "./intl";
import listOfSalesOrder from "./listOfSalesOrder";
import {
  listOfSaleOrderTrackingUrl,
  listOfSaleOrderUrl
} from "./listOfSalesOrder/urls";
import { NotFound } from "./NotFound";
import POUploadSection from "./poUpload";
import { poUploadUrl } from "./poUpload/urls";
import { privacyPolicyUrl } from "./privacyPolicy/urls";
import PrivacyPolicy from "./privacyPolicy/views";
// import RequiredAttentionSection from "./requiredAttention";
// import { requiredAttentionSection } from "./requiredAttention/urls";
import SaveDraftSection from "./saveDraft";
import { saveDraftUrl } from "./saveDraft/urls";
import errorTracker from "./services/errorTracking";
import themeOverrides from "./themes/themeOverrides";
import UserManagementSection from "./userManagement";
import { userManagementSection } from "./userManagement/urls";
import { addError } from "./utils/datadog";

if (process.env.GTM_ID) {
  TagManager.initialize({ gtmId: GTM_ID });
}

errorTracker.init();
Amplify.configure(AWS_CONFIG);
const App: React.FC = () => (
  <SaleorProvider client={saleorClient}>
    <ApolloProvider client={apolloClient}>
      <BrowserRouter basename={APP_MOUNT_URI}>
        <ThemeProvider overrides={themeOverrides}>
          <DateProvider>
            <LocaleProvider>
              <MessageManagerProvider>
                <ServiceWorker />
                <BackgroundTasksProvider>
                  <AppStateProvider>
                    <AuthProvider>
                      <ShopProvider>
                        <ExternalAppProvider>
                          <ExitFormDialogProvider>
                            <Routes />
                          </ExitFormDialogProvider>
                        </ExternalAppProvider>
                      </ShopProvider>
                    </AuthProvider>
                  </AppStateProvider>
                </BackgroundTasksProvider>
              </MessageManagerProvider>
            </LocaleProvider>
          </DateProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ApolloProvider>
  </SaleorProvider>
);

const Routes: React.FC = () => {
  const intl = useIntl();
  const [, dispatchAppState] = useAppState();
  const { isAuthenticated, user, tokenFake, authInProgress } = useAuth();
  const { tokenRefreshApp, logout } = useUser();
  const notify = useNotifier();
  const [jwtToken, setJwtToken] = useState<string | null>("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirectUrl = params.get("redirect");
    sessionStorage.setItem("redirectUrl", redirectUrl || "");
  }, []);

  const homePageLoaded = isAuthenticated;

  const handleLogin = () => {
    removeTokens();
    location.reload();
  };

  const tokenExpiredAndRedirectToHomePage = async () => {
    notify({
      status: "error",
      sourceSystem: "Eordering",
      apiMessage: intl.formatMessage(commonMessages.sessionExpired)
    });

    logout();
  };

  const findJwtTokenExpirationTimeInMilliseconds = (token: string) => {
    const decodedToken = JSON.parse(atob(token.split(".")[1]));
    const expiredTimeInMilliseconds = decodedToken?.exp * 1000;
    return expiredTimeInMilliseconds;
  };

  useEffect(() => {
    setJwtToken(getTokens().auth);
  }, [getTokens().auth]);

  useEffect(() => {
    let timerIdInternal: NodeJS.Timeout = null;
    const COMPUTATION_BUFFER = 1000;

    if (jwtToken) {
      const expiredTimeInMilliseconds = findJwtTokenExpirationTimeInMilliseconds(
        jwtToken
      );

      const diffTime = expiredTimeInMilliseconds - Date.now();

      timerIdInternal = setTimeout(() => {
        if (expiredTimeInMilliseconds + COMPUTATION_BUFFER < Date.now()) {
          tokenExpiredAndRedirectToHomePage().catch(e => console.error(e));
        }
      }, diffTime);
    }

    return () => clearTimeout(timerIdInternal);
  }, [jwtToken]);

  const onIdle = () => {
    tokenExpiredAndRedirectToHomePage().catch(e => console.error(e));
  };

  const onAction = (event?: Event) => {
    if (event.type === "mousedown" || event.type === "keydown") {
      if (jwtToken) {
        const expiredTimeInMilliseconds = findJwtTokenExpirationTimeInMilliseconds(
          jwtToken
        );

        const expiredTimeInMillisecondsDecrease =
          expiredTimeInMilliseconds - 15000;

        if (expiredTimeInMillisecondsDecrease < Date.now()) {
          tokenRefreshApp();
        }
      }
    }
  };

  const {
    pendingOrderPermission,
    deliveryPermission,
    orderConfirmPermission,
    pendingOrderTrackingPermission,
    notRefCreateOrderTrackingPermission,
    listOfSaleOrderTrackingPermission,
    excelUploadPermission
  } = useDerivedPermissions();

  const isCustomer = useMemo(
    () =>
      user?.userPermissions?.find(
        e => e.code === PermissionEnum.ALL_REPORT_CUSTOMER
      ),
    [user]
  );

  return (
    <>
      <WindowTitle title={intl.formatMessage(commonMessages.dashboard)} />
      {DEMO_MODE && <DemoBanner />}
      {homePageLoaded ? (
        <IdleTimerProvider
          timeout={IDLE_TIMEOUT_MINUTE * 60 * 1000}
          onIdle={onIdle}
          onAction={onAction}
        >
          <CustomerBlockContextProvider>
            <LayoutProvider>
              <AppLayout>
                <ErrorBoundary
                  onError={e => {
                    const errorId = errorTracker.captureException(e);
                    addError(e);
                    dispatchAppState({
                      payload: {
                        error: "unhandled",
                        errorId
                      },
                      type: "displayError"
                    });
                  }}
                >
                  <Switch>
                    <SectionRoute exact path="/" component={MainPage} />
                    <SectionRoute
                      path={saveDraftUrl}
                      component={SaveDraftSection}
                      matchPermission="any"
                      permissions={[PermissionEnum.VIEW_DRAFT_ORDER]}
                    />
                    <SectionRoute
                      path={inquirySearchOrderUrl}
                      component={Inquiry}
                      matchPermission="any"
                      permissions={[PermissionEnum.VIEW_ORDER_CUSTOMER]}
                    />
                    <SectionRoute
                      path={createOrderUrl}
                      component={CreateOrderSection}
                      matchPermission="any"
                      permissions={[
                        PermissionEnum.CREATE_ORDER_CUSTOMER,
                        PermissionEnum.CREATE_ORDER_DOMESTIC,
                        PermissionEnum.CREATE_ORDER_EXPORT
                      ]}
                    />
                    <SectionRoute
                      path={createOrderNotRefUrl}
                      component={CreateOrderNotRefSection}
                      matchPermission="any"
                      permissions={notRefCreateOrderTrackingPermission}
                    />
                    <SectionRoute
                      path={changeOrderNotRefUrl}
                      component={CreateOrderNotRefSection}
                      matchPermission="any"
                      permissions={[PermissionEnum.CHANGE_ORDER_DOMESTIC]}
                    />
                    <SectionRoute
                      path={changeOrderUrl}
                      component={ChangeOrderSection}
                      matchPermission="any"
                      permissions={[
                        // PermissionEnum.VIEW_ORDER_CUSTOMER,
                        PermissionEnum.VIEW_ORDER_DOMESTIC,
                        PermissionEnum.VIEW_ORDER_EXPORT,
                        // PermissionEnum.CHANGE_ORDER_CUSTOMER,
                        PermissionEnum.CHANGE_ORDER_DOMESTIC,
                        PermissionEnum.CHANGE_ORDER_EXPORT
                      ]}
                    />
                    {
                      <SectionRoute
                        permissions={[PermissionEnum.MAINTAIN_ALT_MATERIAL]}
                        path={alternatedMaterialUrl}
                        component={AlternatedMaterialSection}
                      />
                    }
                    {/* <SectionRoute*/}
                    {/*  path={requiredAttentionSection}*/}
                    {/*  component={RequiredAttentionSection}*/}
                    {/*  matchPermission="any"*/}
                    {/*  permissions={[*/}
                    {/*    PermissionEnum.VIEW_ORDER_EXPORT,*/}
                    {/*    PermissionEnum.VIEW_ORDER_DOMESTIC*/}
                    {/*  ]}*/}
                    {/*/ >*/}
                    <SectionRoute
                      matchPermission="any"
                      permissions={[
                        PermissionEnum.ALL_UPLOAD_PO_ADMIN,
                        PermissionEnum.ALL_UPLOAD_PO_CUSTOMER
                      ]}
                      path={poUploadUrl}
                      component={POUploadSection}
                    />
                    <SectionRoute
                      path={excelUploadUrl}
                      component={ExcelUploadPage}
                      permissions={excelUploadPermission}
                      matchPermission="any"
                    />
                    <SectionRoute
                      permissions={[PermissionEnum.MAINTAIN_CUSTOMER_MATERIAL]}
                      path={customerMaterialUrl}
                      component={CustomerMaterialSection}
                    />
                    <SectionRoute
                      permissions={[PermissionEnum.MAINTAIN_USER]}
                      path={userManagementSection}
                      component={UserManagementSection}
                    />
                    <SectionRoute
                      permissions={[
                        PermissionEnum.ALL_REPORT_DOMESTIC,
                        PermissionEnum.ALL_REPORT_EXPORT,
                        PermissionEnum.VIEW_STOCK
                      ]}
                      path={listOfSaleOrderUrl}
                      component={listOfSalesOrder}
                      matchPermission="any"
                      exact
                      key={listOfSaleOrderUrl}
                    />
                    <SectionRoute
                      permissions={listOfSaleOrderTrackingPermission}
                      path={listOfSaleOrderTrackingUrl}
                      component={listOfSalesOrder}
                      matchPermission="any"
                      exact
                      key={listOfSaleOrderTrackingUrl}
                    />
                    <SectionRoute
                      matchPermission="any"
                      exact
                      path={privacyPolicyUrl}
                      component={PrivacyPolicy}
                    />
                    <SectionRoute
                      // permissions={[PermissionEnum.MANAGE_SETTINGS]}
                      path={deliveryReport}
                      component={DeliveryReport}
                      permissions={
                        isCustomer && !user?.isSuperuser
                          ? [PermissionEnum.ALL_REPORT_CUSTOMER]
                          : deliveryPermission
                      }
                      matchPermission="any"
                    />
                    <SectionRoute
                      path={orderConfirmUrl}
                      component={OrderConfirmSection}
                      permissions={
                        isCustomer && !user?.isSuperuser
                          ? [PermissionEnum.ALL_REPORT_CUSTOMER]
                          : orderConfirmPermission
                      }
                      matchPermission="any"
                    />
                    {isCustomer && !user?.isSuperuser ? (
                      <SectionRoute
                        path={pendingOrderUrl}
                        component={PendingOrderSection}
                      />
                    ) : (
                      <SectionRoute
                        permissions={pendingOrderPermission}
                        matchPermission="any"
                        path={pendingOrderUrl}
                        component={PendingOrderSection}
                      />
                    )}
                    <SectionRoute
                      path={pendingOrderTrackingUrl}
                      component={PendingOrderSection}
                      key={pendingOrderTrackingUrl}
                      permissions={pendingOrderTrackingPermission}
                      matchPermission="any"
                    />
                    <SectionRoute
                      path={stockOnHandUrl}
                      component={StockOnHandSection}
                    />
                    <Route component={NotFound} />
                  </Switch>
                </ErrorBoundary>
              </AppLayout>
            </LayoutProvider>
          </CustomerBlockContextProvider>
        </IdleTimerProvider>
      ) : !user && tokenFake ? (
        <>
          <WindowTitle title={intl.formatMessage(sectionNames.apps)} />
          <HomePageComponent />
          <DialogCreateOrderError
            onConfirm={handleLogin}
            open={true}
            okText={"ปิดหน้าต่าง"}
            title={"Your account has no permission"}
            content={
              <span>
                <MyTypography variant="body2">
                  Please contact: (+66) 0 2555 6666
                </MyTypography>
              </span>
            }
          />
        </>
      ) : authInProgress ? (
        <></>
      ) : (
        <Auth />
      )}
    </>
  );
};

render(<App />, document.getElementById("dashboard-app"));
